'use strict'
const EXPIRATION_HOURS = 1; // 만료 시간 정의 (Hour)

export default class RecentNoticeService {
  constructor() {
    this.userid          = window.Laravel.user;
    this.localStorageKey = 'recentNotices';
    this.recentNotices   = this.getStoredNotices();
  }

  /**
   * @description 로컬스토리지에 저장된 공지글, 답글 확인 여부 리스트 가져오기
   * @returns {any|string}
   */
  getStoredNotices() {
    return JSON.parse(localStorage.getItem(this.localStorageKey)) ?? '';
  }

  /**
   * @description 로컬스토리지에 저장
   * @param notices
   */
  saveNotices(notices) {
    localStorage.setItem(this.localStorageKey, JSON.stringify(notices));
  }

  /**
   * @description 미확인 공지글, 답글 개수 가져오기
   * @returns {Promise<{countRecentNotices: number, countRecentinquiries: number, countRecentBoards: number}|{countRecentNotices: number, countRecentinquiries: number, countRecentBoards: number}>}
   */
  async init() {
    // 로컬스토리지에 저장된 것이 없거나 만료 시간 도래하면 새로 갱신처리
    if (!this.recentNotices || (this.recentNotices.expiration && new Date().getTime() > this.recentNotices.expiration)) {
      await this.loadByRecentNotices();
    }

    return this.getRecentNoticeCount(this.recentNotices) ?? {
      countRecentNotices  : 0,
      countRecentinquiries: 0,
      countRecentBoards   : 0
    };
  }

  /**
   * @description 미확인 공지글 개수
   * @param recentNotice
   * @returns {{countRecentNotices: number, countRecentinquiries: number, countRecentBoards: number}}
   */
  getRecentNoticeCount(recentNotice = null) {
    const recent               = recentNotice || this.recentNotices;
    const countN               = obj => Object.values(obj ?? {}).filter(val => val === 'N').length;
    const countRecentNotices   = countN(recent.notice);
    const countRecentinquiries = countN(recent.inquiry_reply);
    const countRecentBoards    = countRecentNotices + countRecentinquiries;

    return {
      countRecentNotices,
      countRecentinquiries,
      countRecentBoards
    };
  }

  /**
   * @description 로컬스토리지에 저장되지 않았거나, 만료시간 경과 시 공지글 / 답글 확인여부 리스트 가져와서 등록 처리
   * @param recentNotice
   * @returns {Promise<void>}
   */
  async loadByRecentNotices(recentNotice = '') {
    const userid        = this.userid;
    const newExpiration = Date.now() + (EXPIRATION_HOURS * 60 * 60 * 1000);

    // 최근 31일 내 등록된 공지글, 답글 정보 반영 (확인한 것은 Y, 미확인한 것은 N)
    const response = await axios.post(`${process.env.MIX_BACK_URL}/api/getRecentNotices/init`, {
      userid,
      newExpiration
    }, {
      headers: { Authorization: 'Bearer ' + window.Laravel.access_token }
    });
    // 갱신하여 로컬스토리지에 업데이트 처리
    console.log("update recent notices");
    this.recentNotices = response.data;
    this.saveNotices(this.recentNotices);
  }

  /**
   * @description 읽은 공지글 혹은 답글 번호 배열로 반환
   * @param type
   * @param recentNotice
   * @returns {string[]|*[]}
   */
  getAlreadyReadNo(type = 'notice', recentNotice = '') {
    const recent = recentNotice || this.getStoredNotices();

    if (!recent) {
      return [];
    }
    // 기본 : 읽은 공지글
    let alreadyReadNo = recent ? Object.entries(recent.notice).filter(([key, val]) => val === 'Y').map(([key]) => key) : [];
    // 읽은 답글
    if (type === 'inquiry_reply') {
      alreadyReadNo   = recent ? Object.entries(recent.inquiry_reply).filter(([key, val]) => val === 'Y').map(([key]) => key) : [];
    }

    return alreadyReadNo;
  }

  /**
   * @description 읽은 공지글, 답글 확인으로 갱신처리
   * @param type
   * @param list
   */
  updateRecentInquiries(type = 'notice', list = []) {
    const userid            = this.userid;
    const compareRecentList = this.getStoredNotices(); // 이미 확인해서 업데이트 된 글인지 비교하기 위한 변수
    let recent              = this.getStoredNotices();
    let updateFlag          = true;

    if (recent && typeof recent === 'object') {
      let recentList        = recent[type] ?? null;

      if (!recentList) {
        return;
      }

      for (let key in recentList) {

        // 미확인 글만 체크
        if (recentList[key] !== 'N') {
          // S : 불필요한 DB update 로직 타지 않도록 처리
          if (type === 'notice' && (compareRecentList[type][list.no] === undefined || compareRecentList[type][list.no] === 'Y')) {
            updateFlag = false;
          }
          if (type === 'inquiry_reply' && Array.isArray(list) && list.some(item => compareRecentList[type][item.no] === 'Y')) {
            const cntY = list.some(item => compareRecentList[type][item.no] === 'Y').length;
            updateFlag = cntY !== list.length;
          }
          // E : 불필요한 DB update 로직 타지 않도록 처리

          continue;
        }

        let exists = false;

        // 읽은 글이 공지글일 때
        if (type === 'notice') {
          exists = String(list.no) === String(key);

          // 읽은 글이 답글일 때
        } else if (type === 'inquiry_reply') {
          exists = Array.isArray(list) && list.some(item => item.no === key);
        }

        if (exists) {
          recentList[key] = 'Y';
        }
      }

      // 이미 읽었던 글을 다시 접근할 경우 불필요한 DB update 로직 타지 않도록 처리
      if (updateFlag === false) {
        return;
      }

      recent[type] = recentList;

      // 확인한 데이터 DB에 갱신
      axios.post(`${process.env.MIX_BACK_URL}/api/getRecentNotices/update`, {
        userid,
        recent,
      }, {
        headers: { Authorization: 'Bearer ' + window.Laravel.access_token }
      }).then(response => {
          if (response.data.expiration > 0) {
            this.recentNotices = response.data;
            this.saveNotices(this.recentNotices);
          }
      });
    }

  }


}
